import { useState, useMemo } from 'react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useCollectionBinding } from '@vibesites/runtime/react';

interface VirtualTour {
  virtual_tour_title?: string | null;
  virtual_tour_url?: string | null;
}

export function VirtualTourEmbed() {
  const tours = useCollectionBinding<VirtualTour>('virtualtourembed.tours', {
    collection: 'virtual_tours',
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  const validTours = useMemo(
    () => (tours || []).filter((t) => t.virtual_tour_url),
    [tours],
  );

  const activeTour = validTours[selectedIndex] || validTours[0];
  const hasMultiple = validTours.length > 1;

  return (
    <section className="bg-white py-8 md:py-12">
      {/* Tour selector */}
      {hasMultiple && (
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 mb-4 flex justify-end">
          <div className="relative w-full max-w-xs">
            <select
              value={selectedIndex}
              onChange={(e) => setSelectedIndex(Number(e.target.value))}
              className="appearance-none w-full bg-[var(--vibe-color-primary)] text-white font-body text-lg tracking-wide px-6 py-5 pr-14 border-0 focus:outline-none focus:ring-2 focus:ring-white/40 cursor-pointer truncate"
            >
              {validTours.map((tour, i) => (
                <option
                  key={i}
                  value={i}
                  className="bg-[var(--vibe-color-primary)] text-white"
                >
                  {tour.virtual_tour_title || `Tour ${i + 1}`}
                </option>
              ))}
            </select>
            <svg
              aria-hidden="true"
              className="pointer-events-none absolute right-6 top-1/2 -translate-y-1/2 h-5 w-5 text-white"
              viewBox="0 0 20 20"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M5 8l5 5 5-5" />
            </svg>
          </div>
        </div>
      )}

      {/* Tour embed */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {activeTour?.virtual_tour_url ? (
          <div className="relative w-full rounded-lg overflow-hidden border border-[var(--vibe-color-border)]" style={{ paddingBottom: '56.25%' }}>
            <iframe
              key={activeTour.virtual_tour_url}
              src={activeTour.virtual_tour_url}
              className="absolute inset-0 w-full h-full border-0"
              allowFullScreen
              title={activeTour.virtual_tour_title || 'Virtual Tour'}
            />
          </div>
        ) : (
          <div className="aspect-video w-full bg-[var(--vibe-color-surface)] rounded-lg flex items-center justify-center">
            <p className="text-[var(--vibe-color-text-muted)] font-body">
              No virtual tours are available for this property.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'VirtualTourEmbed',
  displayName: 'Virtual Tour',
  tokens: {},
  collectionBindings: {
    'virtualtourembed.tours': {
      label: 'Virtual Tours',
      collection: 'virtual_tours',
    },
  },
};

(VirtualTourEmbed as any).contentSchema = contentSchema;

export default VirtualTourEmbed;
